
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Application } from "@/types/application";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Settings, UserPlus, Tags, Eye, Trash2 } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import CustomerTag from "@/components/CustomerTag";
import { getAllCustomers } from "@/utils/customerLookup";
import { ConfigureApplicationDialog } from "./ConfigureApplicationDialog";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { TagsDisplay } from "@/components/tags/TagsDisplay";
import { DataTable } from "@/components/ui/data-table";
import { ColumnDef, ColumnSizingState } from "@tanstack/react-table";
import { SortableHeader } from "@/components/ui/table-cells";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function formatBytes(bytes: string | number | undefined): string {
  if (!bytes) return "—";
  const b = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;
  if (isNaN(b) || b === 0) return "—";
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

interface ApplicationListProps {
  applications: Application[];
  onUpdateApplication?: (app: Application) => void;
  onRemoveApplication?: (appId: string) => void;
}

export const ApplicationList = ({ 
  applications,
  onUpdateApplication,
  onRemoveApplication
}: ApplicationListProps) => {
  const navigate = useNavigate();
  const [configAppId, setConfigAppId] = useState<string | null>(null);
  const [tagAssignmentAppId, setTagAssignmentAppId] = useState<string | null>(null);
  const [deleteAppId, setDeleteAppId] = useState<string | null>(null);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  
  const handleConfigureClick = (appId: string) => {
    setConfigAppId(appId);
  };
  
  const handleConfigDialogClose = () => {
    setConfigAppId(null);
  };
  
  const getConfiguredApp = () => {
    return applications.find(app => app.id === configAppId);
  };
  
  const handleSaveConfig = (updatedApp: Application) => {
    if (onUpdateApplication) {
      onUpdateApplication(updatedApp);
    }
    setConfigAppId(null);
  };

  const getTagAssignmentApp = () => {
    return applications.find(app => app.id === tagAssignmentAppId);
  };

  const columns: ColumnDef<Application>[] = [
    {
      accessorKey: "name",
      header: ({ column }) => <SortableHeader column={column}>Application</SortableHeader>,
      cell: ({ row }) => {
        const app = row.original;
        return (
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate(`/applications/${app.id}`)}
          >
            <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden">
              <img src={app.icon} alt={app.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="font-medium">{app.name}</div>
              <div className="text-xs text-muted-foreground">{app.bundleId}</div>
            </div>
          </div>
        );
      },
      size: 300,
    },
    {
      accessorKey: "version",
      header: ({ column }) => <SortableHeader column={column}>Version</SortableHeader>,
      cell: ({ row }) => <div>{row.getValue("version")}</div>,
      size: 120,
    },
    {
      accessorKey: "size",
      header: ({ column }) => <SortableHeader column={column}>Size</SortableHeader>,
      cell: ({ row }) => <div>{formatBytes(row.getValue("size"))}</div>,
      size: 100,
    },
    {
      accessorKey: "platform",
      header: ({ column }) => <SortableHeader column={column}>Platform</SortableHeader>,
      cell: ({ row }) => (
        <Badge variant="outline">{row.getValue("platform")}</Badge>
      ),
      size: 120,
    },
    {
      accessorKey: "customers",
      header: "Assigned to",
      cell: ({ row }) => {
        const app = row.original;
        const customerNames = app.customers
          .map(id => getAllCustomers().find(c => c.id === id)?.name || "")
          .filter(name => name !== "");
        
        return (
          <div className="flex flex-wrap gap-1 max-w-[200px]">
            {customerNames.length > 0 ? (
              customerNames.map((name, i) => (
                <CustomerTag key={i} customer={name} />
              ))
            ) : (
              <span className="text-xs italic text-muted-foreground">No assignments</span>
            )}
          </div>
        );
      },
      size: 200,
    },
    {
      id: "tags",
      header: "Tags",
      cell: ({ row }) => {
        const app = row.original;
        return (
          <TagsDisplay resourceId={app.id} resourceType="application" maxTags={2} size="sm" showRemoveButton />
        );
      },
      size: 180,
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const app = row.original;
        return (
          <div className="flex items-center justify-end">
            <ActionsDropdown
              actions={[
                {
                  label: "Publish",
                  icon: ArrowRight,
                  onClick: () => {
                    toast.success("Application published");
                  },
                },
                {
                  label: "View Details",
                  icon: Eye,
                  onClick: () => navigate(`/applications/${app.id}`),
                },
                {
                  label: "Assign to Customers",
                  icon: UserPlus,
                  onClick: () => {
                    if (onUpdateApplication) {
                      onUpdateApplication(app);
                    }
                  },
                },
                {
                  label: "Assign Tags",
                  icon: Tags,
                  onClick: () => setTagAssignmentAppId(app.id),
                },
                {
                  label: "Configure",
                  icon: Settings,
                  onClick: () => handleConfigureClick(app.id),
                },
                {
                  label: "Delete",
                  icon: Trash2,
                  onClick: () => setDeleteAppId(app.id),
                  destructive: true,
                },
              ]}
            />
          </div>
        );
      },
      size: 200,
    },
  ];

  return (
    <div className="w-full min-w-0">
      <DataTable
        data={applications}
        columns={columns}
        enableSorting={true}
        enableColumnResizing={true}
        columnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        emptyMessage="No applications found"
      />
      
      {configAppId && getConfiguredApp() && (
        <ConfigureApplicationDialog
          application={getConfiguredApp()!}
          open={!!configAppId}
          onOpenChange={handleConfigDialogClose}
          onSave={handleSaveConfig}
        />
      )}

      {tagAssignmentAppId && getTagAssignmentApp() && (
        <TagAssignmentDialog
          open={!!tagAssignmentAppId}
          onOpenChange={(open) => !open && setTagAssignmentAppId(null)}
          resourceId={getTagAssignmentApp()!.id}
          resourceType="application"
          resourceName={getTagAssignmentApp()!.name}
          onAssignmentComplete={() => {
            toast.success("Tags assigned successfully");
            setTagAssignmentAppId(null);
          }}
        />
      )}

      <AlertDialog open={!!deleteAppId} onOpenChange={(open) => !open && setDeleteAppId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Application</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this application? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteAppId && onRemoveApplication) {
                  onRemoveApplication(deleteAppId);
                }
                setDeleteAppId(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
